import React from "react";
import ReactDOM from "react-dom/client";
import * as AnimationModule from "./animations";
import * as ComponentsModule from "./components";

export function addListeners() {
  // Add a listener for earlier injection to prevent CLS
  document.addEventListener("DOMContentLoaded", function () {
    AnimationModule.start();
    renderReactBlocks(ComponentsModule.getComponents());
  });

  // Add a requestAnimationFrame call for even earlier scanning before full page load
  requestAnimationFrame(() => {
    if (document.readyState === "loading") {
      // If still loading, set up mutation observer to detect when components are added
      setupMutationObserver();
    } else {
      // If already interactive or complete, render immediately
      renderReactBlocks(ComponentsModule.getComponents());
    }
  });

  document.addEventListener("liquid-ajax-cart:request-end", (event) => {
    renderReactBlocks(ComponentsModule.getComponents());
  });
}

// Set up mutation observer to detect when component containers are added to the DOM
function setupMutationObserver() {
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        const components = ComponentsModule.getComponents();
        if (components.length) {
          renderReactBlocks(components);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Disconnect after a reasonable time to avoid performance issues
  setTimeout(() => observer.disconnect(), 10000);
}

export function renderReactBlocks(component_list) {
  let node = "";

  // Add logger to track performance
  const perfLog = {
    logs: [],
    startTime: performance.now(),
    logStart: function (component, nodeIndex) {
      const start = performance.now();
      this.logs.push({
        component,
        nodeIndex,
        start,
        props: null,
        render: null,
        transition: null,
        end: null,
        total: null,
      });
      return this.logs.length - 1;
    },
    logProps: function (index, time) {
      if (this.logs[index]) {
        this.logs[index].props = time - this.logs[index].start;
      }
    },
    logRender: function (index, time) {
      if (this.logs[index]) {
        this.logs[index].render = time - this.logs[index].start;
      }
    },
    logTransition: function (index, time) {
      if (this.logs[index]) {
        this.logs[index].transition = time - this.logs[index].start;
      }
    },
    logEnd: function (index, time) {
      if (this.logs[index]) {
        this.logs[index].end = time;
        this.logs[index].total = time - this.logs[index].start;
      }
    },
    printSummary: function () {
      // console.group("React Component Rendering Performance");
      // console.log(`Total components processed: ${this.logs.length}`);
      // console.log(
      //   `Total render time: ${(performance.now() - this.startTime).toFixed(2)}ms`
      // );

      if (this.logs.length > 0) {
        // Create table data
        const tableData = this.logs.map((log) => ({
          Component: log.component,
          Node: log.nodeIndex,
          "Props Parse (ms)": log.props?.toFixed(2) || "N/A",
          "Render (ms)": log.render?.toFixed(2) || "N/A",
          "Transition (ms)": log.transition?.toFixed(2) || "N/A",
          "Total (ms)": log.total?.toFixed(2) || "N/A",
        }));

        // console.table(tableData);

        // Show slowest components
        const sorted = [...this.logs].sort(
          (a, b) => (b.total || 0) - (a.total || 0)
        );
        // console.log("Slowest components:");
        sorted.slice(0, 3).forEach((log, i) => {
          // console.log(
          //   `${i + 1}. ${log.component} (node ${log.nodeIndex}): ${log.total?.toFixed(
          //     2
          //   )}ms`
          // );
        });
      }
      console.groupEnd();
    },
  };

  function decodeHTMLEntities(text) {
    const entities = {
      "&amp;": "&",
      "&quot;": '"',
      "&#39;": "'",
      "&lt;": "<",
      "&gt;": ">",
      "&nbsp;": " ",
      // Add more entities if necessary
    };
    return text.replace(/&[^\s;]+;/g, (match) => entities[match] || match);
  }

  function stripHTMLTags(text) {
    const doc = new DOMParser().parseFromString(text, "text/html");
    return doc.body.textContent || "";
  }

  function sanitizeJSON(jsonString) {
    try {
      // LEE MATTHEWS HANDLING OF DESCRIPTION CODE FOR DATA CLEANSING
      jsonString = jsonString.replace(
        /data-sheets-(root|userformat|value)=['][^']*[']\s*/g,
        ""
      );

      // Remove line breaks, tabs, and extra spaces
      let sanitizedString = jsonString
        .replace(/\\n/g, "")
        .replace(/\\t/g, "")
        .replace(/<br \/>/g, "<br/>")
        .trim();
      // Decode HTML entities
      sanitizedString = decodeHTMLEntities(sanitizedString);

      sanitizedString = sanitizedString.replaceAll('="', "='");
      sanitizedString = sanitizedString.replaceAll('">', "'>");
      sanitizedString = sanitizedString.replaceAll('=\\"', "='");
      sanitizedString = sanitizedString.replaceAll('\\"', "'");
      sanitizedString = sanitizedString.replaceAll("\\'", "'");

      // Remove invalid trailing commas
      sanitizedString = sanitizedString.replace(/,\s*([\]}])/g, "$1");
      return sanitizedString;
    } catch (error) {
      console.error(`Error sanitizing JSON string: ${error.message}`);
      return null;
    }
  }

  // Function to preserve dimensions of an element to prevent CLS
  function preserveDimensions(element) {
    if (!element) return;

    // Get computed dimensions
    const styles = window.getComputedStyle(element);
    const width = styles.width;
    const height = styles.height;
    const display = styles.display;

    // Only apply if we have meaningful dimensions
    if (
      width !== "auto" &&
      height !== "auto" &&
      width !== "0px" &&
      height !== "0px"
    ) {
      element.style.minWidth = width;
      element.style.minHeight = height;

      // Ensure the element uses the right display model
      if (display === "none") {
        element.style.display = "block";
      }

      // Return cleanup function
      return () => {
        element.style.minWidth = "";
        element.style.minHeight = "";
      };
    }

    return () => {};
  }

  try {
    let propsDataJSON = null;
    const totalStartTime = performance.now();

    const allNodes = Array.from(document.querySelectorAll("[data-theme-id]"));

    for (let index = 0; index < allNodes.length; index++) {
      const currentNode = allNodes[index];
      const componentName = currentNode.getAttribute("data-theme-id");
      const componentDef = component_list.find(comp => comp.name === componentName);

      if (!componentDef || currentNode.getAttribute("injected")) continue;

      const logIndex = perfLog.logStart(componentName, index);
      const cleanup = preserveDimensions(currentNode);
      currentNode.classList.add("react-component-loading");

      let node_data_props = null;
      var injected = currentNode.getAttribute("injected");

      if (!injected) {
        // currentNode is already defined at the top of the loop

        const propsStartTime = performance.now();
        let propsNode =
          currentNode.parentElement.querySelector("#react-data");

        if (propsNode) {
          propsDataJSON = propsNode.getAttribute("data-injected-props");

          const sanitizedPropsDataJSON = sanitizeJSON(propsDataJSON);

          if (sanitizedPropsDataJSON) {
            try {
              node_data_props = JSON.parse(sanitizedPropsDataJSON);
              perfLog.logProps(logIndex, performance.now());
            } catch (parseError) {
              console.log(sanitizedPropsDataJSON);
              console.error(
                `JSON parse error on node ${node}: ${parseError.message}`
              );
              continue;
            }
            propsNode.remove();
          } else {
            console.error(`Sanitization failed for node ${node}`);
            continue;
          }
        } else {
          if (currentNode.getAttribute("data-block-props")) {
            propsDataJSON = currentNode.getAttribute("data-block-props");

            const sanitizedPropsDataJSON = sanitizeJSON(propsDataJSON);

            if (sanitizedPropsDataJSON) {
              try {
                node_data_props = JSON.parse(sanitizedPropsDataJSON);
                perfLog.logProps(logIndex, performance.now());
              } catch (parseError) {
                console.error(
                  `JSON parse error on node ${node}: ${parseError.message}`
                );
                continue;
              }
              currentNode.remove();
            } else {
              console.error(`Sanitization failed for node ${node}`);
              continue;
            }
          } else {
            perfLog.logProps(logIndex, performance.now());
          }
        }

        const Element = componentDef.element;

        if (Element) {
          // Create a wrapper component that handles the fade-in transition
          const ComponentWithTransition = (props) => {
            React.useEffect(() => {
              const renderTime = performance.now();
              perfLog.logRender(logIndex, renderTime);

              // Use a reference to the actual DOM node instead of looking it up by ID
              if (currentNode) {
                // Ensure component is mounted before transitioning
                requestAnimationFrame(() => {

                  perfLog.logTransition(logIndex, performance.now());

                  // Clean up dimension preservation after transition completes
                  setTimeout(() => {

                    perfLog.logEnd(logIndex, performance.now());
                  }, 100); // Match this with CSS transition duration
                });
              }

              return () => {
                // Cleanup if component unmounts
                if (currentNode) {

                  perfLog.logEnd(logIndex, performance.now());
                }
              };
            }, []);

            return <Element {...props} />;
          };

          try {
            const renderStartTime = performance.now();
            ReactDOM.createRoot(currentNode).render(
              <ComponentWithTransition settings={node_data_props} />
            );
            // console.log(
            //   `${componentName}[${index}] initial render: ${(
            //     performance.now() - renderStartTime
            //   ).toFixed(2)}ms`
            // );

            if (currentNode) {
              currentNode.setAttribute(
                "id",
                componentName + "-injected"
              );
              currentNode.setAttribute("injected", true);
              currentNode.removeAttribute("data-block-props");
            }
          } catch (renderError) {
            console.error(
              `Error rendering component ${componentName}:`,
              renderError
            );
            cleanup(); // Clean up if rendering failed
            currentNode.classList.remove("react-component-loading");
            perfLog.logEnd(logIndex, performance.now());
          }
        } else {
          console.error(`Error received on component ${componentName}`);
          cleanup(); // Clean up if component failed to load
          currentNode.classList.remove("react-component-loading");
          perfLog.logEnd(logIndex, performance.now());
        }
      }
    }

    // Print performance summary after all components are processed
    // console.log(
    //   `Total render processing time: ${(
    //     performance.now() - totalStartTime
    //   ).toFixed(2)}ms`
    // );
    // Set timeout to ensure we capture the final transitions
    setTimeout(() => perfLog.printSummary(), 500);
  } catch (e) {
    console.error(`Error on node ${node}`, e.message);
    console.error(e.message);
    console.error("Error Injecting React Components");
  }
}
